import type { DbClient } from '@fintree/supabase/db-client'
import { defaultDbClient } from '@fintree/supabase/db-client'
import { logActivity, type ActivityActor, withActivityActor } from '../internal/activity'
import type { Candidate, CandidateInsert } from './dto'
import { calculateProfileScore } from './reads'

export async function upsertCandidate(
  payload: Partial<Candidate> & { phone: string },
  actor: ActivityActor,
  client: DbClient = defaultDbClient
): Promise<Candidate> {
  const phone = payload.phone.trim()
  if (!phone) throw new Error('phone is required')

  const { data: student } = await client
    .from('students')
    .select('name')
    .is('deleted_at', null)
    .eq('phone', phone)
    .maybeSingle()
  const studentName = student?.name ?? null

  const { data: existing } = await client.from('candidates').select('*').eq('phone', phone).maybeSingle()

  const merged: Partial<Candidate> = {
    name: payload.name ?? existing?.name ?? studentName,
    current_position: payload.current_position ?? existing?.current_position ?? null,
    experience_years: payload.experience_years ?? existing?.experience_years ?? null,
    exp_band: payload.exp_band ?? existing?.exp_band ?? null,
    education: payload.education ?? existing?.education ?? null,
    skills: payload.skills ?? existing?.skills ?? null,
    resume_url: payload.resume_url ?? existing?.resume_url ?? null,
    resume_filename: payload.resume_filename ?? existing?.resume_filename ?? null,
    linkedin_url: payload.linkedin_url ?? existing?.linkedin_url ?? null,
    linkedin_data: payload.linkedin_data ?? existing?.linkedin_data ?? null,
    availability: payload.availability ?? existing?.availability ?? null,
    placement_status: payload.placement_status ?? existing?.placement_status ?? null,
    total_applications: payload.total_applications ?? existing?.total_applications ?? null,
    total_shortlists: payload.total_shortlists ?? existing?.total_shortlists ?? null,
    total_placements: payload.total_placements ?? existing?.total_placements ?? null,
  }

  const profile_score = calculateProfileScore(merged)

  const insertOrUpdate: CandidateInsert = {
    phone,
    name: merged.name ?? null,
    current_position: merged.current_position ?? null,
    experience_years: merged.experience_years ?? null,
    exp_band: merged.exp_band ?? null,
    education: merged.education ?? null,
    skills: merged.skills ?? null,
    resume_url: merged.resume_url ?? null,
    resume_filename: merged.resume_filename ?? null,
    linkedin_url: merged.linkedin_url ?? null,
    linkedin_data: merged.linkedin_data ?? null,
    availability: merged.availability ?? null,
    placement_status: merged.placement_status ?? null,
    total_applications: merged.total_applications ?? null,
    total_shortlists: merged.total_shortlists ?? null,
    total_placements: merged.total_placements ?? null,
    profile_score,
  }
  if (payload.reliability_score !== undefined) insertOrUpdate.reliability_score = payload.reliability_score
  if (payload.times_applied !== undefined) insertOrUpdate.times_applied = payload.times_applied
  if (payload.times_no_show !== undefined) insertOrUpdate.times_no_show = payload.times_no_show
  if (payload.times_withdrawn !== undefined) insertOrUpdate.times_withdrawn = payload.times_withdrawn

  let result: Candidate
  if (existing) {
    const { data, error } = await client
      .from('candidates')
      .update(insertOrUpdate)
      .eq('phone', phone)
      .select('*')
      .single()
    if (error) throw error
    result = data
  } else {
    const { data, error } = await client.from('candidates').insert(insertOrUpdate).select('*').single()
    if (error) throw error
    result = data
  }

  await logActivity(
    withActivityActor(actor, {
      action: 'UPSERT_CANDIDATE',
      entityType: 'candidates',
      entityId: phone,
      oldValues: existing ? (JSON.parse(JSON.stringify(existing)) as Record<string, unknown>) : undefined,
      newValues: JSON.parse(JSON.stringify(result)) as Record<string, unknown>,
    }),
    client
  )

  return result
}
